import React, { useEffect, useRef, useState } from "react";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import "./DebouncedSearchInput.css";

interface Props {
  /** Fired with the debounced text once typing settles. */
  onChange: (value: string) => void;
  initialValue?: string;
  delayMs?: number;
  placeholder?: string;
  className?: string;
  "aria-label"?: string;
}

/**
 * Search box that keeps the raw keystrokes in local state and only surfaces the
 * debounced value to its parent — so typing stays instant no matter how heavy
 * the parent's render is. It owns no external value; to clear it on a context
 * switch (e.g. a new schedule), remount it with a changed `key`.
 */
const DebouncedSearchInput: React.FC<Props> = ({
  onChange,
  initialValue = "",
  delayMs = 200,
  placeholder,
  className,
  "aria-label": ariaLabel,
}) => {
  const [text, setText] = useState(initialValue);
  const debounced = useDebouncedValue(text, delayMs);
  const mounted = useRef(false);

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onChange(debounced);
  }, [debounced, onChange]);

  return (
    <input
      type="text"
      className={["debounced-search-input", className].filter(Boolean).join(" ")}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={text}
      onChange={(e) => setText(e.target.value)}
    />
  );
};

export default DebouncedSearchInput;
